/**
 * Sends athletes to their own page on the first page they load after signing in.
 *
 * WHY THIS EXISTS RATHER THAN A PORTAL SETTING
 * Liferay's default.landing.page.path is portal-wide: it cannot tell an
 * athlete from a coach. Setting it to /athlete would send coaching staff to a
 * page that refuses them on every login, and leaving it at /home does nothing
 * for the 180 people this is built for.
 *
 * The correct Liferay mechanism is a post-login LifecycleAction setting
 * WebKeys.LAST_PATH from the user's roles. That is better engineering —
 * server-side, before anything renders, no flash — but it is a custom OSGi
 * module, which this re-platform exists to avoid. The trade taken here is a
 * brief flash of the landing page in exchange for keeping the whole stack as
 * client extensions.
 *
 * PLACED ON THE HOME PAGE. Liferay returns a user to whatever page they were
 * on before signing in (auth.forward.by.last.path), verified in the access
 * log: signing in from /athlete lands on /athlete, and from /home lands on
 * /home. So there is no single landing page to target, and only two cases
 * matter — an athlete arriving at /athlete is already where they belong, and
 * one arriving at Home is the case this handles.
 *
 * Global JS would cover every page and needs no placement, but Liferay would
 * not inject it: the entry registers and approves, and nothing appears in the
 * page. Whatever activates it is not in Site Settings, the site's type
 * settings, or portlet preferences. Worth revisiting; not worth blocking on.
 *
 * THREE RULES, EACH LOAD-BEARING
 *
 * 1. ONCE PER SESSION. The first signed-in page load redirects; nothing after
 *    it does. An athlete who then navigates anywhere — including Home — stays
 *    where they went. A landing page you cannot leave is a cage, and this runs
 *    on the athlete's own phone.
 *
 * 2. NEVER FROM THE ATHLETE PAGES. If the first page is already /athlete or
 *    below it, there is nothing to do. Redirecting /athlete/results to
 *    /athlete would take a deep link away from the person who followed it.
 *
 * 3. NO COST TO ANYONE ELSE. Guests are never asked. Staff are asked once and
 *    the answer is cached for the session.
 *
 * It renders nothing — no overlay, no "redirecting…" — because that would put
 * a flash on every page for every user to smooth one moment for one group.
 *
 * KNOWN TRADE: an athlete who follows a deep link to a non-athlete page as the
 * first thing they do in a session gets moved to /athlete once. Distinguishing
 * "arrived by login" from "arrived by link" is not reliable in the browser,
 * and sending an athlete to their own page is a mild wrong answer where
 * stranding them on a coach page is a worse one.
 */

/** Where athletes go. Must match the friendly URL of the athlete page. */
const ATHLETE_PATH = "/athlete";

/**
 * Storage keys, SCOPED TO THE SIGNED-IN USER **AND THEIR LOGIN SESSION**.
 *
 * sessionStorage survives signing out and back in within the same tab, so
 * un-keyed flags belonged to whoever used the tab first. On a shared device —
 * a tablet in a changing room, which is exactly what this is for — the first
 * athlete would be redirected and the second would not, and the cached "is an
 * athlete" answer would still be the first person's.
 *
 * THE USER ID ALONE WAS NOT ENOUGH, and the gap showed up the moment anybody
 * signed in twice: the same athlete, in the same tab, found their own
 * `decided` flag from the previous login still set, so decide() returned
 * before it ever asked whether they were an athlete and left them on the coach
 * Home page. Diagnosed from the gateway log — /compute/me was never called at
 * all, which only happens on an early return.
 *
 * authToken is Liferay's per-session CSRF token, so a fresh login produces a
 * fresh key and a clean decision. If it is ever rotated mid-session the cost
 * is one extra redirect from Home, which is the behaviour this exists to
 * provide anyway.
 */
function sessionMark(): string {
  const token = window.Liferay?.authToken ?? "";
  /* Short and non-reversible enough for a storage key. The token is already in
     the page for anyone who can read this, so this is about key length rather
     than secrecy. */
  let hash = 0;
  for (let i = 0; i < token.length; i += 1) {
    hash = (hash * 31 + token.charCodeAt(i)) | 0;
  }
  return token ? String(hash >>> 0) : "no-token";
}

function keyFor(name: string): string {
  const userId = window.Liferay?.ThemeDisplay?.getUserId?.() ?? "0";
  return `cl:landing:${name}:${userId}:${sessionMark()}`;
}

/** True when the current page is the athlete area — nothing to do there. */
function onAthletePage(): boolean {
  const path = window.location.pathname;
  return path === ATHLETE_PATH || path.startsWith(`${ATHLETE_PATH}/`);
}

/**
 * Waits until Liferay reports a signed-in session, or gives up.
 *
 * ThemeDisplay EXISTING is not the same as ThemeDisplay being populated.
 * Checking isSignedIn() the moment the global appears returned false for a
 * signed-in athlete, decide() returned immediately, and the redirect never
 * happened — with no error and no flag written, so it looked like the widget
 * had not run at all.
 *
 * Resolving false is the ordinary case for a guest, who has nobody to
 * redirect, so this cannot simply wait forever.
 */
function awaitSignedIn(timeoutMs = 5000): Promise<boolean> {
  const check = () => Boolean(window.Liferay?.ThemeDisplay?.isSignedIn?.());
  if (check()) return Promise.resolve(true);

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (check()) {
        window.clearInterval(timer);
        resolve(true);
      } else if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, 50);
  });
}

async function isAthlete(): Promise<boolean> {
  const cached = sessionStorage.getItem(keyFor("isAthlete"));
  if (cached !== null) return cached === "true";

  try {
    const response = await fetch("/ai/compute/me", {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        // Required exactly as for every other gateway call — without it the
        // gateway cannot establish identity and answers 401.
        "x-csrf-token": window.Liferay?.authToken ?? "",
      },
    });

    /*
     * 200 means the caller resolved to an athlete record. 403 means signed in
     * and not one; 401 means the session is unusable.
     *
     * 409 MEANS A PARENT WITH MORE THAN ONE CHILD, and it belongs with 200
     * rather than with the refusals. The gateway answers it when a stakeholder
     * holds several grants and has not said which — so the caller certainly
     * HAS an athlete page to be on. They have simply not chosen whose yet, and
     * the picker that asks them lives on that page.
     *
     * Read as a refusal it stranded exactly the person the feature was built
     * for: a parent of siblings signed in, was left on the coach Home page,
     * and had no route to the app at all. A parent of ONE child was fine,
     * which is the sort of difference nobody reports clearly.
     */
    const athlete = response.status === 200 || response.status === 409;
    sessionStorage.setItem(keyFor("isAthlete"), String(athlete));
    return athlete;
  } catch {
    // A gateway that cannot be reached is not a reason to move anyone. Fail by
    // leaving the user exactly where they asked to be.
    return false;
  }
}

/**
 * Did this page load come straight from signing in?
 *
 * THE SESSION FLAG WAS NOT ENOUGH, and the reason is worth stating. It exists
 * so an athlete who deliberately navigates to Home is left there — a landing
 * page you cannot leave is a cage. But it also meant that signing out and back
 * in within the same tab found the previous login's flag and skipped the
 * redirect, which is exactly the moment an athlete most expects to be taken to
 * their own page.
 *
 * A referrer of the sign-in page is an unambiguous "this load IS the login",
 * and it beats the flag. Everything else keeps the once-per-session rule, so
 * the cage is still not built.
 *
 * Liferay's own sign-in lives at /sign-in and /c/portal/login; both are matched
 * because which one you came through depends on how you got there.
 */
function arrivedFromSignIn(): boolean {
  const from = document.referrer;
  if (!from) return false;
  try {
    const url = new URL(from);
    if (url.origin !== window.location.origin) return false;
    return url.pathname === "/sign-in" || url.pathname.startsWith("/c/portal/login");
  } catch {
    return false;
  }
}

async function decide(): Promise<void> {
  if (!(await awaitSignedIn())) return;

  /* A login always redirects. Anything else defers to the once-per-session
     rule, so navigating to Home later leaves you on Home. */
  const justSignedIn = arrivedFromSignIn();
  if (!justSignedIn && sessionStorage.getItem(keyFor("decided")) === "true") return;

  // Already where they belong: record the decision so a later click on Home
  // is not treated as the first page of the session.
  if (onAthletePage()) {
    sessionStorage.setItem(keyFor("decided"), "true");
    return;
  }

  // Written BEFORE the redirect, not after: the redirect unloads the page, so
  // a flag set afterwards would never be stored, and the athlete would be
  // bounced every time they tried to leave.
  sessionStorage.setItem(keyFor("decided"), "true");

  if (await isAthlete()) {
    // replace(), not assign(): the page they passed through should not sit in
    // history as somewhere Back can return to.
    window.location.replace(ATHLETE_PATH);
  }
}

/**
 * Waits for Liferay's own script to define the global.
 *
 * Global JS may be injected before Liferay's scripts have run, and this code
 * needs two things from that global: whether anyone is signed in, and the CSRF
 * token. Without waiting, an early evaluation reads `undefined`, concludes
 * "guest", and returns — silently doing nothing on exactly the page load that
 * matters, with no error anywhere to explain it.
 *
 * Gives up after a few seconds. Not every page is a portal page, and polling
 * forever on one that will never define it is worse than not running.
 */
function waitForPortal(timeoutMs = 5000): Promise<boolean> {
  if (window.Liferay?.ThemeDisplay) return Promise.resolve(true);

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (window.Liferay?.ThemeDisplay) {
        window.clearInterval(timer);
        resolve(true);
      } else if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, 50);
  });
}

const ELEMENT_NAME = "cl-athlete-landing";

class AthleteLandingElement extends HTMLElement {
  connectedCallback(): void {
    // Waits for the global, then for the session it reports — see the two
    // helpers above. Both are needed: the object appears before it is filled.
    void waitForPortal().then((ready) => {
      if (ready) void decide();
    });
  }
}

if (!customElements.get(ELEMENT_NAME)) {
  customElements.define(ELEMENT_NAME, AthleteLandingElement);
}

declare global {
  interface Window {
    Liferay?: {
      ThemeDisplay?: { isSignedIn?: () => boolean; getUserId?: () => string };
      authToken?: string;
    };
  }
}

export {};
